import React, { useState, useEffect } from 'react';
import { MessageCircle, Lock, Check } from 'lucide-react';
import styles from './form.module.css';
import ParticipantQuestionImage from './ParticipantQuestionImage';

const QAForm = ({ content, onSubmit, isPreview = false, isLocked = false, userAnswer = null, allowUpdateResponse = true }) => {
    const limit = content.settings?.characterLimit
        ? Math.min(1000, Math.max(20, Number(content.settings.charLimitValue) || 160))
        : 500;
    const labelOptions = Array.isArray(content.settings?.labelOptions) && content.settings.labelOptions.length > 0
        ? content.settings.labelOptions
        : ['Technical', 'General', 'Other'];

    const [text, setText] = useState(() => (userAnswer?.text != null ? String(userAnswer.text) : ''));
    const [selectedLabel, setSelectedLabel] = useState(() => (userAnswer?.label != null ? String(userAnswer.label) : (labelOptions[0] || '')));

    useEffect(() => {
        if (userAnswer?.text != null) setText(String(userAnswer.text));
        else if (userAnswer == null) setText('');
    }, [userAnswer?.text, userAnswer == null]);

    useEffect(() => {
        if (userAnswer?.label != null) setSelectedLabel(String(userAnswer.label));
        else if (userAnswer == null && labelOptions.length) setSelectedLabel(labelOptions[0]);
    }, [userAnswer?.label, userAnswer == null, labelOptions]);

    const hasSubmitted = userAnswer != null;
    const optionsDisabled = isLocked || (hasSubmitted && !allowUpdateResponse);
    const isUnchanged = hasSubmitted
        && String(text).trim() === String(userAnswer?.text ?? '').trim()
        && (!content.settings?.labels || selectedLabel === (userAnswer?.label ?? ''));
    const submitLabel = !hasSubmitted
        ? (isPreview ? 'Submit' : isLocked ? 'Voting Locked' : 'Ask')
        : !allowUpdateResponse || isUnchanged ? <>Submitted <Check size={14} strokeWidth={3} aria-hidden /></> : 'Update response';
    const submitDisabled = isPreview || isLocked || !text.trim() || (hasSubmitted && (!allowUpdateResponse || isUnchanged));

    const buildAnswer = () => {
        const ans = { text: text.trim() };
        if (content.settings?.labels && selectedLabel) ans.label = selectedLabel;
        return ans;
    };

    return (
        <div className={`${styles.form} ${styles.qaForm}`}>
            <div className={styles.qaFormCard}>
                <div className={styles.qaFormHeader}>
                    <span className={styles.qaFormIcon} aria-hidden><MessageCircle size={20} /></span>
                    <h2 className={styles.qaFormTitle}>
                        Ask a question
                        {isLocked && <span className={styles.lockBadge}> <Lock size={12} aria-hidden /> Locked</span>}
                    </h2>
                </div>
                {content.settings?.description && content.settings?.descText && (
                    <p className={styles.qaFormDesc}>{content.settings.descText}</p>
                )}
                {content.settings?.imageUrl && (
                    <ParticipantQuestionImage imageUrl={content.settings.imageUrl} alt="Question visual" />
                )}
                {content.settings?.labels && (
                    <div className={styles.qaLabelRow}>
                        <label className={styles.qaLabelLabel}>Category</label>
                        <select
                            className={styles.qaLabelSelect}
                            value={selectedLabel}
                            onChange={(e) => setSelectedLabel(e.target.value)}
                            disabled={optionsDisabled}
                        >
                            {labelOptions.map((opt) => (
                                <option key={opt} value={opt}>{opt}</option>
                            ))}
                        </select>
                    </div>
                )}
                <div className={styles.qaInputWrap}>
                    <textarea
                        className={styles.qaTextArea}
                        disabled={optionsDisabled}
                        placeholder="Type your question for the moderator…"
                        value={text}
                        onChange={e => setText(e.target.value)}
                        maxLength={limit}
                        rows={4}
                    />
                    <div className={styles.qaFormFooter}>
                        <span className={styles.qaCharCount}>{text.length}/{limit}</span>
                        <button
                            className={styles.qaSubmitBtn}
                            onClick={() => onSubmit(buildAnswer())}
                            disabled={submitDisabled}
                        >
                            {submitLabel}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default QAForm;
